import { makeObservable, observable, action, computed } from "mobx";
import { RequestState } from "../constants";
import { userStore } from "../stores";
import { apiService } from "../services";

export class MealPlanController {
  mealPlans = [];
  currentMealPlan = null;
  loadPlansRequestState = RequestState.IDLE;
  generatePlanRequestState = RequestState.IDLE;
  savePlanRequestState = RequestState.IDLE;
  message = "";

  constructor() {
    makeObservable(this, {
      mealPlans: observable,
      currentMealPlan: observable,
      loadPlansRequestState: observable,
      generatePlanRequestState: observable,
      savePlanRequestState: observable,
      message: observable,
      loadUserMealPlans: action,
      generateMealPlan: action,
      saveMealPlan: action,
      setCurrentMealPlan: action,
      setLoadPlansRequestState: action,
      setGeneratePlanRequestState: action,
      setSavePlanRequestState: action,
      setMessage: action,
      initializeMealPlanning: action,
      getLoadPlansRequestState: computed,
      getGeneratePlanRequestState: computed,
      getSavePlanRequestState: computed,
      getMessage: computed,
    });
  }

  get getLoadPlansRequestState() {
    return this.loadPlansRequestState;
  }

  get getGeneratePlanRequestState() {
    return this.generatePlanRequestState;
  }

  get getSavePlanRequestState() {
    return this.savePlanRequestState;
  }

  get getMessage() {
    return this.message;
  }

  setLoadPlansRequestState(state) {
    this.loadPlansRequestState = state;
  }

  setGeneratePlanRequestState(state) {
    this.generatePlanRequestState = state;
  }

  setSavePlanRequestState(state) {
    this.savePlanRequestState = state;
  }

  setMessage(message) {
    this.message = message;
  }

  setCurrentMealPlan(mealPlan) {
    this.currentMealPlan = mealPlan;
  }

  async initializeMealPlanning() {
    if (userStore.isLoggedIn) {
      await this.loadUserMealPlans();
    }
  }

  async loadUserMealPlans() {
    if (!userStore.isLoggedIn) {
      this.setMessage("Please log in to view meal plans");
      return;
    }

    try {
      this.setLoadPlansRequestState(RequestState.REQUESTED);
      this.setMessage("");

      const response = await apiService.getUserMealPlans();

      if (response.status === 200) {
        this.mealPlans = response.data.meal_plans || [];
        this.setLoadPlansRequestState(RequestState.SUCCESS);
      }
    } catch (error) {
      console.error("Error loading meal plans:", error);
      this.setLoadPlansRequestState(RequestState.ERROR);
      this.setMessage(
        error.response?.data?.error ||
          "Error loading meal plans. Please try again."
      );
    }
  }

  async generateMealPlan(planData) {
    if (!userStore.isLoggedIn) {
      this.setMessage("Please log in to generate meal plans");
      return null;
    }

    try {
      this.setGeneratePlanRequestState(RequestState.REQUESTED);
      this.setMessage("");

      const response = await apiService.generateMealPlan(planData);

      if (response.status === 200) {
        const mealPlan = response.data.meal_plan;
        this.setCurrentMealPlan(mealPlan);
        this.setGeneratePlanRequestState(RequestState.SUCCESS);
        this.setMessage("Meal plan generated successfully!");
        return mealPlan;
      }
    } catch (error) {
      console.error("Error generating meal plan:", error);
      this.setGeneratePlanRequestState(RequestState.ERROR);
      this.setMessage(
        error.response?.data?.error ||
          "Error generating meal plan. Please try again."
      );
      return null;
    }
  }

  async saveMealPlan(planData) {
    if (!userStore.isLoggedIn) {
      this.setMessage("Please log in to save meal plans");
      return false;
    }

    try {
      this.setSavePlanRequestState(RequestState.REQUESTED);
      this.setMessage("");

      const response = await apiService.saveMealPlan(planData);

      if (response.status === 200) {
        this.setSavePlanRequestState(RequestState.SUCCESS);
        this.setMessage("Meal plan saved successfully!");

        // Reload the user's meal plans to include the new one
        await this.loadUserMealPlans();

        return true;
      }
    } catch (error) {
      console.error("Error saving meal plan:", error);
      this.setSavePlanRequestState(RequestState.ERROR);
      this.setMessage(
        error.response?.data?.error ||
          "Error saving meal plan. Please try again."
      );
      return false;
    }
  }

  async getMealPlanDetails(planId) {
    if (!userStore.isLoggedIn) {
      this.setMessage("Please log in to view meal plan details");
      return null;
    }

    try {
      const response = await apiService.getMealPlanDetails(planId);

      if (response.status === 200) {
        const mealPlan = response.data.meal_plan;
        this.setCurrentMealPlan(mealPlan);
        return mealPlan;
      }
    } catch (error) {
      console.error("Error getting meal plan details:", error);
      this.setMessage(
        error.response?.data?.error ||
          "Error loading meal plan details. Please try again."
      );
      return null;
    }
  }

  // Helper methods for meal plan management
  getMealsByDay(day) {
    if (!this.currentMealPlan?.meals) return [];
    return this.currentMealPlan.meals.filter((meal) => meal.day === day);
  }

  getTotalCaloriesForDay(day) {
    const meals = this.getMealsByDay(day);
    return meals.reduce((total, meal) => total + (meal.calories || 0), 0);
  }

  getAllIngredients() {
    if (!this.currentMealPlan?.meals) return [];
    const ingredients = new Set();

    this.currentMealPlan.meals.forEach((meal) => {
      meal.ingredients?.forEach((ingredient) => {
        ingredients.add(ingredient);
      });
    });

    return Array.from(ingredients);
  }

  getMealTypes() {
    if (!this.currentMealPlan?.meals) return [];
    const mealTypes = new Set();

    this.currentMealPlan.meals.forEach((meal) => {
      if (meal.meal_type) {
        mealTypes.add(meal.meal_type);
      }
    });

    return Array.from(mealTypes);
  }

  // Analytics methods
  getNutritionSummary() {
    if (!this.currentMealPlan?.meals) return null;

    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      meals: this.currentMealPlan.meals.length,
    };

    this.currentMealPlan.meals.forEach((meal) => {
      totals.calories += meal.calories || 0;
      if (meal.nutrition) {
        totals.protein += meal.nutrition.protein || 0;
        totals.carbs += meal.nutrition.carbs || 0;
        totals.fat += meal.nutrition.fat || 0;
        totals.fiber += meal.nutrition.fiber || 0;
      }
    });

    // Calculate averages per day
    const days = this.currentMealPlan.days || 1;
    return {
      ...totals,
      avgCaloriesPerDay: Math.round(totals.calories / days),
      avgProteinPerDay: Math.round(totals.protein / days),
      avgCarbsPerDay: Math.round(totals.carbs / days),
      avgFatPerDay: Math.round(totals.fat / days),
      avgFiberPerDay: Math.round(totals.fiber / days),
    };
  }
}
